'use client';


interface EvidenceCard {
    label: string;
    context: string;
    goal: string;
    brief: string;
    feltResult: string;
    returnReason: string;
}

const TestimonialsSection = () => {
    const evidenceCards: EvidenceCard[] = [
        {
            label: 'Career clarity',
            context: 'Illustrative scenario',
            goal: 'Decide whether to pitch live or prepare in writing.',
            brief: 'The note favored written decisions, research first, and lower-energy communication later in the day.',
            feltResult: 'Could help someone finish a proposal draft instead of forcing a call.',
            returnReason: 'Timing language can help frame a choice without predicting its outcome.',
        },
        {
            label: 'Skeptical first try',
            context: 'Illustrative scenario',
            goal: 'Test whether the product felt more grounded than a generic horoscope app.',
            brief: 'The reading connected chart timing, numerology cycle, and one practical next step.',
            feltResult: 'A reader can inspect the signal layers instead of relying on mystical certainty.',
            returnReason: 'Showing the signal layers makes the interpretation easier to question.',
        },
        {
            label: 'Morning ritual',
            context: 'Illustrative scenario',
            goal: 'Start the day with one prompt before journaling or planning work.',
            brief: 'The reading ended with one focus, one caution, and one reflection question.',
            feltResult: 'It can work as a short morning check-in before journaling or planning.',
            returnReason: 'A compact format is easier to fit into a daily routine.',
        },
        {
            label: 'Decision support',
            context: 'Illustrative scenario',
            goal: 'Choose what kind of task fit the day instead of asking for a prediction.',
            brief: 'The timing language pointed toward deep work and away from fast promises.',
            feltResult: 'A reader can use the brief to sequence tasks more intentionally.',
            returnReason: 'The format is intended for reflection and planning, not certainty.',
        },
        {
            label: 'Optional palm depth',
            context: 'Illustrative scenario',
            goal: 'See whether palm context added anything beyond chart and numerology.',
            brief: 'The optional palm layer echoed the same energy theme instead of throwing in random extra meaning.',
            feltResult: 'Optional palm context can add another perspective without being required.',
            returnReason: 'Additional context stays optional rather than blocking the core brief.',
        },
        {
            label: 'Low-friction format',
            context: 'Illustrative scenario',
            goal: 'Get something useful in under two minutes.',
            brief: 'The reading closed with one action, one caution, and one reflection instead of a long wall of text.',
            feltResult: 'One action, caution, and reflection can make a reading easier to act on.',
            returnReason: 'A short format can make repeat use more realistic.',
        },
    ];

    const repeatedPatterns = [
        'One focus, one caution, and one reflection',
        'Interpretation paired with visible signal sources',
        'Optional palm context, not a signup requirement',
        'A practical prompt without fixed-fate claims',
    ];

    return (
        <section id="testimonials" className="relative overflow-hidden border-y border-[var(--card-border)] bg-[var(--card-surface)] py-24">
            <div className="container mx-auto px-5 relative z-10">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-16 space-y-4">
                        <h2 className="text-4xl md:text-5xl font-bold text-foreground font-playfair tracking-wide animate-in fade-in slide-in-from-bottom-8 duration-700">
                            Example ways to use a daily brief
                        </h2>
                        <p className="text-xl text-muted-foreground font-light tracking-wide animate-in fade-in slide-in-from-bottom-8 duration-700 delay-100">
                            Illustrative examples of how a brief can support reflection. These are not customer testimonials or measured outcomes.
                        </p>
                    </div>

                    <div className="relative">
                        <div className="grid gap-6 lg:grid-cols-2">
                            {evidenceCards.map((card, index) =>
                            <article
                                    key={index}
                                    className="card-surface manuscript-frame rounded-xl p-6 shadow-card-depth md:p-7"
                                >
                                    <div className="mb-6 border-b border-[var(--experience-border)] pb-4">
                                        <div>
                                            <h3 className="font-bold text-base text-foreground font-playfair">{card.label}</h3>
                                            <p className="mt-1 text-xs uppercase tracking-wider font-medium text-[var(--experience-text-faint)]">{card.context}</p>
                                        </div>
                                    </div>

                                    <div className="space-y-4">
                                        <EvidenceRow label="Goal" copy={card.goal} />
                                        <EvidenceRow label="What the brief surfaced" copy={card.brief} />
                                        <EvidenceRow label="Possible use" copy={card.feltResult} />
                                    </div>

                                    <div className="mt-6 border-t border-[var(--experience-border)] pt-4">
                                        <p className="text-[10px] font-bold uppercase tracking-widest text-accent">Why this format may help</p>
                                        <p className="mt-2 text-sm leading-6 text-[var(--experience-text)]">{card.returnReason}</p>
                                    </div>
                                </article>
                            )}
                        </div>
                    </div>

                    <div className="mt-16 max-w-5xl mx-auto border-t border-[var(--card-border)] pt-10">
                        <p className="text-center text-[11px] text-muted-foreground uppercase tracking-[0.22em] mb-6">
                            Format principles illustrated here
                        </p>
                        <div className="grid gap-3 md:grid-cols-2">
                            {repeatedPatterns.map((pattern) => (
                                <div
                                    key={pattern}
                                    className="rounded-lg border border-[var(--experience-border)] bg-[var(--experience-surface-quiet)] px-5 py-4 text-center text-sm text-[var(--experience-text)]"
                                >
                                    {pattern}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>);
};

function EvidenceRow({ label, copy }: { label: string; copy: string }) {
    return (
        <div>
            <p className="mb-2 text-[10px] uppercase tracking-[0.22em] text-[var(--experience-text-faint)]">{label}</p>
            <p className="text-sm leading-6 text-[var(--experience-text)]">{copy}</p>
        </div>
    );
}

export default TestimonialsSection;
